import { lazy, Suspense, useMemo } from "react";
import { WEB_URL } from "@/lib/webUrl";
import { routes, parentDestinationOptions, sortCzech } from "@ckgo2/shared";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Guide } from "@ckgo2/shared/types";
import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  MapPin,
  Utensils,
  Image,
  Settings,
  HelpCircle,
  ListChecks,
  Link2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEditPage } from "@/hooks/useEditPage";
import { ADMIN_INSPIRATION_LIST_KEY, useAdminDestinationList } from "@/hooks/useAdminLists";
import { useEditorTab, useSwitchTabOnSaveError } from "@/hooks/useEditorTab";
import { DESTINATION_FIELD_TO_TAB } from "@/lib/fieldTabMap";
import ErrorBanner from "@/components/common/ErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import TabWebLink from "@/components/common/TabWebLink";
import { MediaPickerScopeProvider } from "@/components/common/MediaPickerScope";
import type { DestinationFormData } from "@/components/destinations";
import {
  DESTINATION_FORM_DEFAULTS,
  destinationFormFromDetail,
  destinationSaveBody,
  type DestinationDetail,
} from "@/components/destinations/destinationForm";
import { destinationHintRegistry } from "@/lib/webHints/destination";
import { destinationVariant, tabWebTarget } from "@/lib/webHints/variants";
import i18n from "@/i18n";

const BasicTab = lazy(() => import("@/components/destinations/BasicTab"));
const ContentTab = lazy(() => import("@/components/destinations/ContentTab"));
const PracticalTab = lazy(() => import("@/components/destinations/PracticalTab"));
const CuisineTab = lazy(() => import("@/components/destinations/CuisineTab"));
const MediaTab = lazy(() => import("@/components/destinations/MediaTab"));
const FaqTab = lazy(() => import("@/components/destinations/FaqTab"));
const OfferTab = lazy(() => import("@/components/destinations/OfferTab"));
const RelatedTab = lazy(() => import("@/components/destinations/RelatedTab"));

const TABS = [
  { id: "basic", label: i18n.t("catalog.destinations.edit.tabs.basic"), icon: FileText },
  { id: "content", label: i18n.t("catalog.destinations.edit.tabs.content"), icon: MapPin },
  { id: "practical", label: i18n.t("catalog.destinations.edit.tabs.practical"), icon: Settings },
  { id: "cuisine", label: i18n.t("catalog.destinations.edit.tabs.cuisine"), icon: Utensils },
  { id: "media", label: i18n.t("catalog.destinations.edit.tabs.media"), icon: Image },
  { id: "faq", label: i18n.t("catalog.destinations.edit.tabs.faq"), icon: HelpCircle },
  { id: "offer", label: i18n.t("catalog.destinations.edit.tabs.offer"), icon: ListChecks },
  { id: "related", label: i18n.t("catalog.destinations.edit.tabs.related"), icon: Link2 },
];
const TAB_IDS = TABS.map((tab) => tab.id);

export default function DestinationEditPage() {
  const { t } = useTranslation();
  const [activeTab, setTab] = useEditorTab(TAB_IDS);

  // Volby roletky rodiče ze štíhlého ADMIN seznamu (i nepublikované předseedované země
  // smí být rodičem); plný seznam destinací se kvůli roletce nestahuje.
  const { data: destinationListData } = useAdminDestinationList();

  // Průvodci pro roletku `guideId` (`/api/guides` vrací aktivní průvodce česky abecedně, `compareCzech`)
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  const {
    id,
    isNew,
    isLoading,
    isError,
    error: queryError,
    form,
    saveMutation,
    onSubmit,
    queryClient,
    itemData,
  } = useEditPage<DestinationFormData, DestinationDetail>({
    resourceKey: "destination",
    listKey: "admin-destinations",
    apiPath: "/api/admin/destinations",
    defaultValues: DESTINATION_FORM_DEFAULTS,
    navigateTo: "/destinations",
    slugSourceField: "name",
    // Převody jsou čisté funkce (`destinationForm.ts`, test `destinationForm.test.ts`):
    // formulář nese jen zobrazená pole, JSON sloupce celé, a tělo nenese sloupce mimo editor.
    transformOnLoad: destinationFormFromDetail,
    transformOnSave: destinationSaveBody,
    // Bez `resetDeps`: seznamy (destinace, průvodci) do resetu formuláře nepatří — jejich
    // dojetí po invalidaci uložením by formulář znovu resetovalo na data detailu, která
    // ještě nemusí být obnovená. Volbu `<select>` dorovná `useSelectValueSync`.
  });

  useSwitchTabOnSaveError(saveMutation.error, DESTINATION_FIELD_TO_TAB, setTab);

  // `useEditPage` invaliduje sám `["admin-destinations"]` (zasáhne i štíhlý seznam). Uložení
  // destinace ale mění i data jinde, takže po úspěchu (při chybě `onSubmit` vyhodí dál a
  // invalidace neproběhne):
  // - `["destinations"]`: veřejná štíhlá projekce pro roletku Google recenzí
  //   (`useDestinationOptions`, klíč `["destinations","slim"]`);
  // - sdílený seznam inspirací: `syncDestinationInspirations` v API přepíše přiřazeným
  //   i odebraným inspiracím `destinationId`, seznam nese `destinationName`;
  // - detaily inspirací v keši (`["activity", id]`): otevřený ze staré keše by ukázal starou
  //   destinaci a uložení by skončilo falešným konfliktem 409 (`updatedAt` se změnil).
  //   Neaktivní dotaz se jen označí za zastaralý, nic se nestahuje.
  // - detaily OSTATNÍCH destinací (`["destination", id]`, prefix): nesou `inspirationIds`
  //   a přepnutá inspirace z nich zmizela. Detail ze staré keše by ji při dalším uložení
  //   přes `syncDestinationInspirations` tiše vrátil zpátky (bez 409, `updatedAt` té
  //   destinace se nemění). Vlastní detail obnovuje `useEditPage` tak jako tak.
  const onSubmitAndInvalidate = async (data: DestinationFormData) => {
    await onSubmit(data);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["destinations"] }),
      queryClient.invalidateQueries({ queryKey: ["destination"] }),
      queryClient.invalidateQueries({ queryKey: ADMIN_INSPIRATION_LIST_KEY }),
      queryClient.invalidateQueries({ queryKey: ["activity"] }),
    ]);
  };

  // Roletka rodiče: všechny destinace na úrovni země kromě editované; dnešní (uložený)
  // rodič zůstává vždy. Podle `itemData`, ne podle formuláře: volby se nemají měnit pod
  // rukama při výběru.
  const savedParentId = itemData?.parentId ?? null;
  const parentOptions = useMemo(
    () =>
      parentDestinationOptions(
        destinationListData?.data ?? [],
        isNew ? null : (id ?? null),
        savedParentId,
      ),
    [destinationListData, isNew, id, savedParentId],
  );
  const guides = useMemo(() => sortCzech(guidesData?.data ?? [], (g) => g.name), [guidesData]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = form;

  // Varianta podle formuláře: nápovědy a viditelnost polí sledují volbu rodiče hned.
  const variant = destinationVariant({
    parentId: watch("parentId") || null,
    showAsCountry: watch("showAsCountry"),
  });
  // Odkazy „Zobrazit na webu“ podle ULOŽENÉHO detailu (web ukazuje uložený stav): stav
  // publikace a kanonická adresa (`countrySlug` počítá API, admin kopii pravidla nemá).
  const published = !isNew && itemData?.isPublished === true;
  const pageUrl = itemData?.slug
    ? WEB_URL + routes.destinace({ slug: itemData.slug, countrySlug: itemData.countrySlug ?? null })
    : null;
  const tabTarget = tabWebTarget(destinationHintRegistry, activeTab, variant);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400 max-w-md rounded-lg p-4 text-center">
          <p className="font-medium">{t("catalog.destinations.edit.loadFailed")}</p>
          <p className="mt-1 text-sm">
            {queryError?.message || t("catalog.destinations.edit.unknownError")}
          </p>
        </div>
        <Link to="/destinations" className="btn-secondary">
          <ArrowLeft className="h-4 w-4" />
          {t("catalog.destinations.edit.backToList")}
        </Link>
      </div>
    );
  }

  const tabProps = { register, control, errors, variant };

  // Země okna výběru fotek (F7.11): uložená destinace podle sebe, nová podle rodiče z formuláře
  // (nová zemská úroveň ve stromu ještě není, filtr země nemá).
  const pickerDestinationId = isNew
    ? watch("showAsCountry")
      ? null
      : watch("parentId") || null
    : (id ?? null);

  const page = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/destinations"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew
                ? t("catalog.destinations.edit.newTitle")
                : t("catalog.destinations.edit.editTitle")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("catalog.destinations.edit.newSubtitle")
                : t("catalog.destinations.edit.editingSubtitle", { name: itemData?.name ?? "..." })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <TabWebLink
            target={{ anchor: null }}
            pageUrl={pageUrl}
            published={published}
            isNew={isNew}
          />
          <button
            type="button"
            onClick={handleSubmit(onSubmitAndInvalidate)}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/*
        Odkaz záložky na její sekci webu. Cíl `{ page }` (Nabídka) stránka přeskočí: odkaz na
        výpis aktivní plochy kreslí záložka sama, protože plochu zná jen ona.
      */}
      {!("page" in tabTarget) && (
        <div data-tab-web-link={activeTab}>
          <TabWebLink target={tabTarget} pageUrl={pageUrl} published={published} isNew={isNew} />
        </div>
      )}

      {/* Form */}
      <form id="destination-form" onSubmit={handleSubmit(onSubmitAndInvalidate)}>
        <Suspense
          fallback={
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          }
        >
          {activeTab === "basic" && (
            <BasicTab {...tabProps} parentOptions={parentOptions} guides={guides} />
          )}
          {activeTab === "content" && <ContentTab {...tabProps} />}
          {activeTab === "practical" && <PracticalTab {...tabProps} />}
          {activeTab === "cuisine" && <CuisineTab {...tabProps} />}
          {activeTab === "media" && <MediaTab {...tabProps} />}
          {activeTab === "faq" && <FaqTab {...tabProps} />}
          {activeTab === "offer" && (
            <OfferTab
              {...tabProps}
              destinationId={isNew ? null : (id ?? null)}
              slug={itemData?.slug ?? ""}
              published={published}
              isNew={isNew}
              watch={watch}
            />
          )}
          {activeTab === "related" && (
            <RelatedTab {...tabProps} destinationId={isNew ? undefined : id} />
          )}
        </Suspense>
      </form>
    </div>
  );

  return (
    <MediaPickerScopeProvider destinationId={pickerDestinationId}>{page}</MediaPickerScopeProvider>
  );
}
